import PopupModal from "components/general/PopupModal";
import SlidePage from "components/general/SlidePage";
import { Box, Button, H4, ParagraphSmall, SafeAreaView, ScrollView } from "components/ui";
import React, { useState, useEffect, useRef } from "react";
import { Item, useGetItemQuery } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { ItemAlternativeDisplay } from "./ItemAlternativeDisplay";

type Props = {
	item: Item;
	isStockList?: boolean;
};

export const ItemAlternativeButton = ({ item, isStockList = false }: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isOpenMobile, setIsOpenMobile] = useState(false);
	const [hasHovered, setHasHovered] = useState(false);
	const isMobile = useIsMobile();
	const historyStateAdded = useRef(false);

	const { data, isLoading, isFetching } = useGetItemQuery(
		{
			stockcodeCollection: item.substituteCollection as string[],
		},
		{ 
			skip: isMobile 
				? !isOpenMobile 
				: !isOpen && !hasHovered 
		}
	);

	// Handle browser back button and escape key
	useEffect(() => {
		const isModalOpen = isMobile ? isOpenMobile : isOpen;

		const handlePopState = () => {
			if (isModalOpen && historyStateAdded.current) {
				if (isMobile) {
					setIsOpenMobile(false);
				} else {
					setIsOpen(false);
				}
				historyStateAdded.current = false;
			}
		};

		const handleEscapeKey = (event: KeyboardEvent) => {
			if (event.key === "Escape" && isModalOpen) {
				if (isMobile) {
					setIsOpenMobile(false);
				} else {
					setIsOpen(false);
				}
				// Don't call history.back() here - let the cleanup effect handle it
			}
		};

		if (isModalOpen) {
			// Add history state when modal opens
			if (typeof window !== "undefined" && !historyStateAdded.current) {
				window.history.pushState({ modalOpen: true }, "", window.location.href);
				historyStateAdded.current = true;
			}

			window.addEventListener("popstate", handlePopState);
			document.addEventListener("keydown", handleEscapeKey);
		}

		return () => {
			window.removeEventListener("popstate", handlePopState);
			document.removeEventListener("keydown", handleEscapeKey);
		};
	}, [isOpen, isOpenMobile, isMobile]);

	// Clean up history state when modal closes normally
	useEffect(() => {
		const isModalOpen = isMobile ? isOpenMobile : isOpen;

		if (!isModalOpen && historyStateAdded.current && typeof window !== "undefined") {
			// Modal was closed normally (not via back button), so we need to remove the history state
			window.history.back();
			historyStateAdded.current = false;
		}
	}, [isOpen, isOpenMobile, isMobile]);

	const mobileModalHandler = () => {
		setIsOpenMobile(!isOpenMobile);
	};

	const modalHandler = () => {
		setIsOpen(!isOpen);
	};

	if (item.substituteCollection?.length == 0) {
		return null;
	}

	if (isMobile) {
		return (
			<SafeAreaView>
				<Button paddingHorizontal={0} justifyContent="flex-start" onPress={() => mobileModalHandler()}>
					<ParagraphSmall sx={{ textDecorationLine: "underline" }} weight="normal">
						See alternative
					</ParagraphSmall>
				</Button>
				<SlidePage headerTitle="Product Alternatives" isOpen={isOpenMobile} toggleModal={mobileModalHandler}>
					<ScrollView paddingHorizontal={24} sx={{ width: "100%" }}>
						<Box justifyContent="center" alignItems="center" w="$full" h={74} borderWidth={1} borderRadius={12}>
							<H4>{item.name}</H4>
						</Box>
						{data?.list?.map((item, index) => <ItemAlternativeDisplay key={index} item={item} />)}
					</ScrollView>
				</SlidePage>
			</SafeAreaView>
		);
	}

	return (
		<Box>
			<Button
				onPress={() => {
					modalHandler();
				}}
				onHoverIn={() => {
					setHasHovered(true);
				}}
			>
				<ParagraphSmall weight="normal" sx={{ color: "$primarylight", textDecorationLine: "underline", marginRight: isStockList ? "auto" : 0 }}>
					See alternative
				</ParagraphSmall>
			</Button>
			<PopupModal isLoading={isFetching} animationIn={"fadeIn"} animationOut={"fadeOut"} scrollOffset={1} onBackdropPress={() => setIsOpen(false)} isVisible={isOpen} title="Product Alternatives">
				<Box justifyContent="center" alignItems="center" w="$full" h={74} borderWidth={1} borderRadius={12}>
					<H4>{item.name}</H4>
				</Box>
				{data?.list?.map((item, index) => <ItemAlternativeDisplay key={index} item={item} />)}
			</PopupModal>
		</Box>
	);
};
